
import TripFilterList from '@components/my-trips/TripFilterList.vue';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class';
import { FilterOptions, LoadState } from '@util/types';
import PositionMap from '@components/my-trips/PositionMap.vue'
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import TripsOverview from '@components/my-trips/TripsOverview.vue';
import TripsFilterModal from '@/components/my-trips/trips-overview/TripsFilterModal.vue';
import ExportImportModal from '@/components/my-trips/trips-overview/export-import-modal/ExportImportModal.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import AppVehicleHeader from '@components/new-styleguide/AppVehicleHeader.vue';
import TripStatisticBar from '@components/new-styleguide/TripStatisticBar.vue';
import { findEarliestTripInList, findLatestTripInList } from '@/utils/tripUtils'
import {
  TripLog,
  TripsSummary,
  Location,
  Trip,
  TripError,
  SuggestedFuelStop,
  GisFavoriteSynchronizationResultResultEnum,
  TripsPage,
  Device,
} from '@clients/myTrips'
import { deepEquals } from '@/utils/copy';
import { RouterQuery } from '@/router/Routes';
import { subMonths } from 'date-fns';

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings')
const RouterModule = namespace('Router');

@Component({
  name: 'MyTrips',
  components: {
    TripFilterList,
    TripsOverview,
    InfoBoxWarning,
    PositionMap,
    ExportImportModal,
    TripsFilterModal,
    AppPage,
    AppContent,
    AppVehicleHeader,
    TripStatisticBar,
  },
})
export default class MyTrips extends Vue {
  @TriplogModule.Mutation('RESET_TRIP_ERRORS')
  private resetTripErrors;

  @TriplogModule.Mutation('RESET_SUGGESTED_FUEL_STOPS')
  private resetSuggestedFuelStops;

  @TriplogModule.Getter('getVehiclesStatus')
  private vehiclesStatus: LoadState;

  @TriplogModule.Getter('getTripsStatus')
  private loadTripsStatus: LoadState;

  @TriplogModule.Getter('getTripErrorsStatus')
  private loadTripErrorsStatus: LoadState;

  @TriplogModule.Getter('getFilterOptions')
  private filterOptions: FilterOptions;

  @TriplogModule.Getter('getVehicleSummary')
  private vehicleSummary: TripsSummary;

  @TriplogModule.Getter('getTripsAreMoreAvailable')
  private areMoreTripsAvailable;

  @TriplogModule.Getter('getVehicleSummaryStatus')
  private vehicleSummaryStatus: LoadState;

  @TriplogModule.Getter('getLatestFilteredLocation')
  private latestFilteredLocation: Location;

  @TriplogModule.Getter('getDevice')
  private device: Device;

  @TriplogModule.Getter('getTrips')
  private trips: Trip[];

  @TriplogModule.Getter('getSuggestedFuelStops')
  private suggestedFuelStops: SuggestedFuelStop[];

  @TriplogModule.Getter('getSuggestedFuelStopsStatus')
  private suggestedFuelStopsStatus: LoadState;

  @TriplogModule.Getter('getLatestTrip')
  private latestTrip: Trip;

  @TriplogModule.Getter('getTripErrors')
  private tripErrors: TripError[];

  @TriplogModule.Action('fetchVehicleSummary')
  private fetchVehicleSummary;

  @TriplogModule.Action('fetchTrips')
  private fetchTrips;

  @TriplogModule.Action('fetchDevice')
  private fetchDevice;

  @TriplogModule.Action('fetchSuggestedFuelStops')
  private fetchSuggestedFuelStops;

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  @TriplogModule.Action('fetchOldestTrip')
  private fetchOldestTrip;

  @TriplogModule.Action('fetchTripErrors')
  private fetchTripErrors;

  @TriplogModule.Mutation('SET_FILTER_OPTIONS')
  private setFilterOptions;

  @TriplogModule.Mutation('RESET_GET_TRIPS')
  private resetGetTrips;

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Action('fetchQuickActionTags')
  private fetchQuickActionTags;

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Getter('triggerGisFavoriteSynchronizationStatus')
  private triggerGisFavoriteSynchronizationStatus;

  @SettingsModule.Getter('getGisFavoriteSynchronizationResult')
  private gisFavoriteSynchronizationResult;

  @RouterModule.State('query')
  private query: RouterQuery;

  @TriplogModule.Getter('getSelectedTriplog')
  private triplog: TripLog;

  private hasNoVehicleError = false;

  private showExportImportModal = false;

  private showFilterModal = false;

  private showImportModal = false;

  private lastKnownDeviceLocation: Location | null = null

  showTripErrors() {
    this.showExportImportModal = false;
    this.submitFilters(this.filterOptions.asErrorsOnly());
  }

  private openFilters() {
    this.showFilterModal = true;
  }

  private openExport() {
    this.showExportImportModal = true;
  }

  async created() {
    if (deepEquals(this.filterOptions, FilterOptions.emptyFilters())) {
      this.setFilterOptions(new FilterOptions({
        date: {
          from: this.query.from || null,
          until: this.query.until || null
        },
        tagId: this.query.tag ? parseInt(this.query.tag as string) : null,
        pilotId: this.query.pilot ? parseInt(this.query.pilot as string) : null,
        zoneId: this.query.zone ? parseInt(this.query.zone as string) : null,
        errorsOnly: !!this.query.errorsOnly
      }));
    }

    if (this.vehiclesStatus === LoadState.Success && this.loadTripsStatus !== LoadState.Success) {
      this.handleTriplogInput()
    }

    this.updateQueryParams();
  }

  private async refreshPage() {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      for (let registration of registrations) {
        registration.update();
      }
    }
    window.location.reload();
  }

  get zonesHaveChangedAfterGisFavoriteSynchronization(): boolean {
    return this.gisFavoriteSynchronizationResult.result === GisFavoriteSynchronizationResultResultEnum.Changed
  }

  @Watch('gisFavoriteSynchronizationResult')
  private reloadTripsIfZonesHaveChanged() {
    if (this.zonesHaveChangedAfterGisFavoriteSynchronization) {
      this.handleTriplogInput();
    }
  }

  private closeFilterModal() {
    this.showFilterModal = false;
  }

  private closeExportImportModal() {
    this.showExportImportModal = false;
  }

  private resetTrips() {
    this.resetGetTrips();
    this.fetchTrips({ vehicleId: this.vehicleIdOrNull });
  }

  private handleTriplogInput() {
    if (this.filterOptions.errorsOnly) {
      this.fetchMoreTripErrors()
    } else {
      this.refetchTrips();
    }

    if (this.triplog) {
      this.fetchLatestTrip({ vehicleId: this.triplog.vehicle.id });
      this.fetchOldestTrip({ vehicleId: this.triplog.vehicle.id });
    }

    this.fetchQuickActionTags();
    this.fetchPilots();
    this.fetchRules();
    this.fetchTags();
    this.fetchZones();
  }

  private submitFilters(filtersOptions: FilterOptions) {
    this.setFilterOptions(filtersOptions);
    this.updateQueryParams();
    if (filtersOptions.errorsOnly) {
      this.resetTripErrors();
      this.resetSuggestedFuelStops();
      this.resetTripErrorDateFilter();
      return this.fetchMoreTripErrors();
    } else {
      return this.refetchTrips();
    }
  }

  private async refetchTrips() {
    this.resetTripErrors();
    this.resetSuggestedFuelStops();
    this.resetGetTrips();

    return await Promise.all([
      this.fetchVehicleSummary(this.filters),
      this.fetchMoreTrips()
    ])
  }

  private loadOnBottomScroll() {
    if (this.filterOptions.errorsOnly) {
      this.fetchMoreTripErrors()
    } else if (this.areMoreTripsAvailable && this.loadTripsStatus === LoadState.Success && this.vehicleSummaryStatus === LoadState.Success) {
      this.fetchMoreTrips();
    }
  }

  private async fetchSuggestedFuelStopsForVehicle(from?: string, to?: string, append = false) {
    if (this.vehicleIdOrNull) {
      const dateFrom = from || this.filterOptions.date.from
      const dateTo = to || this.filterOptions.date.until
      return this.fetchSuggestedFuelStops({ vehicleId: this.vehicleIdOrNull, from: dateFrom, until: dateTo, append})
    }
    return []
  }

  private async fetchMoreTrips(): Promise<TripsPage> {
    const tripsPage = await this.fetchTrips(this.filters) as TripsPage;

    if (!this.areCategoryFiltersActive && this.vehicleIdOrNull) {
      const firstTrip = findEarliestTripInList(tripsPage.trips)
      const lastTrip = findLatestTripInList(tripsPage.trips)
      this.fetchTripErrors({
        vehicleId: this.vehicleIdOrNull,
        from: firstTrip?.startInfo?.timestamp,
        until: lastTrip?.endInfo?.timestamp,
        append: true
      })
      this.fetchSuggestedFuelStopsForVehicle(
        firstTrip?.startInfo?.timestamp,
        lastTrip?.endInfo?.timestamp,
        true
      )
    }

    return tripsPage
  }

  private updateQueryParams() {
    const query = {
      ...this.query,
      from: this.filters.filterFrom || undefined,
      until: this.filters.filterUntil || undefined,
      tag: this.filters.filterTagId || undefined,
      pilot: this.filters.filterPilotId || undefined,
      zone: this.filters.filterZoneId || undefined,
      errorsOnly: this.filterOptions.errorsOnly ? 'true' : undefined
    }

    if (!deepEquals(query, this.query)) {
      this.$router.replace({ query });
    }
  }

  get filters() {
    return {
      vehicleId: this.vehicleIdOrNull,
      filterFrom: this.filterOptions.date.from,
      filterUntil: this.filterOptions.date.until,
      filterTagId: this.filterOptions.tagId ? this.filterOptions.tagId.toString() : undefined,
      filterPilotId: this.filterOptions.pilotId ? this.filterOptions.pilotId.toString() : undefined,
      filterZoneId: this.filterOptions.zoneId ? this.filterOptions.zoneId.toString() : undefined,
    }
  }

  get isVehicleSummaryLoading() {
    return this.vehicleSummaryStatus !== LoadState.Success;
  }

  get areCategoryFiltersActive() {
    return this.filterOptions.categoryFiltersActive;
  }

  get areFiltersActive() {
    return this.filterOptions.filtersActive;
  }

  get suggestedFuelStopsOrEmpty() {
    return this.suggestedFuelStopsStatus === LoadState.Success ? this.suggestedFuelStops : [];
  }

  get isLoadingFirstTrips() {
    return (this.loadTripsStatus === LoadState.Loading
    || this.loadTripsStatus === LoadState.None
    || this.loadTripErrorsStatus === LoadState.Loading
    ) && this.trips.length === 0;
  }

  get entitiesStatus() {
    return [this.vehiclesStatus, this.loadTripsStatus];
  }

  get shouldLoadEntities() {
    return this.entitiesStatus.some(status => status !== LoadState.Success);
  }

  get fullscreenMap() {
    return this.query.fullscreenMap === 'true';
  }

  get backLocation() {
    if (this.fullscreenMap) {
      return { query: { ...this.query, fullscreenMap: undefined } };
    }
  }

  get isLoading() {
    return this.entitiesStatus.some(status => status === LoadState.Loading);
  }

  get hasLoadError() {
    return this.entitiesStatus.some(status => status === LoadState.Error);
  }

  get vehicleIdOrNull() {
    return this.triplog ? this.triplog.vehicle.id : undefined;
  }

  get selectedVehicle() {
    return this.triplog?.vehicle
  }

  get lastKnownVehicleLocation() {
    return this.lastKnownDeviceLocation || this.latestFilteredLocation
  }

  get isLatestTripOngoing() {
    return this.latestTrip && !(this.latestTrip.endInfo?.timestamp)
  }

  latestTripFetchIntervalId: ReturnType<typeof setInterval | null>  = null
  static readonly LATEST_TRIP_FETCH_INTERVALS = {
    VEHICLE_MOVING: 10000,
    VEHICLE_PARKED: 90000
  } as const

  get latestTripFetchInterval() {
    return this.isLatestTripOngoing ?
      MyTrips.LATEST_TRIP_FETCH_INTERVALS.VEHICLE_MOVING :
      MyTrips.LATEST_TRIP_FETCH_INTERVALS.VEHICLE_PARKED
  }

  clearLatestTripFetchInterval() {
    if (this.latestTripFetchIntervalId) {
      clearInterval(this.latestTripFetchIntervalId)
      this.latestTripFetchIntervalId = null
    }
  }

  async fetchLatestTripAndLocationIfOngoing () {
    await this.fetchLatestTrip({ vehicleId: this.selectedVehicle?.id, forceFetch: true })
    if (this.isLatestTripOngoing) {
      this.fetchDevice({ deviceId: this.selectedVehicle?.deviceId })  // also refetch device to update last known location
    }
  }

  @Watch("selectedVehicle", {immediate: true})
  private async periodicallyRefreshLatestTrip() {
    this.clearLatestTripFetchInterval()
    if (this.selectedVehicle?.deviceId) {
      this.fetchLatestTripAndLocationIfOngoing()
      this.latestTripFetchIntervalId = setInterval(
        this.fetchLatestTripAndLocationIfOngoing,
        this.latestTripFetchInterval
      )
    }
  }

  beforeDestroy() {
    this.clearLatestTripFetchInterval()
  }

  @Watch("latestTrip")
  private onUpdateLatestTrip(newTrip: Trip, oldTrip: Trip) {
    if (!!newTrip?.endInfo?.timestamp !== !!oldTrip?.endInfo?.timestamp) {
      this.periodicallyRefreshLatestTrip()
    }
  }

  @Watch("device")
  private updateLastKnownLocation() {
    let { latitude, longitude } = this.device?.lastKnownLocation ?? {}
    if (latitude != null && longitude != null) {
      this.lastKnownDeviceLocation = this.device.lastKnownLocation
    }
  }

  private tripErrorLoadConfig = {
    from: null as Date | null,
    until: null as Date | null,
    allLoaded: false,
    pageIntervalInMonths: 12,
    maxRefetchCount: 3,
    minPageSize: 15
  }

  private computeNextTripErrorDateFilter () {
    let { from, until, pageIntervalInMonths } = this.tripErrorLoadConfig
    if (from == null || until == null) {
      from = new Date()
    }
    until = new Date(from)
    from = subMonths(until, pageIntervalInMonths)

    this.tripErrorLoadConfig.from = from
    this.tripErrorLoadConfig.until = until
    return {
      from,
      until
    }
  }

  private resetTripErrorDateFilter () {
    this.tripErrorLoadConfig.from = null
    this.tripErrorLoadConfig.until = null
    this.tripErrorLoadConfig.allLoaded = false
  }

  private async fetchMoreTripErrors () {
    if (this.filterOptions.errorsOnly && this.vehicleIdOrNull && !this.tripErrorLoadConfig.allLoaded) {
      // if a date filter has been specified, just one simple fetch call with the filters will take place:
      if (this.filterOptions.date.from || this.filterOptions.date.until) {
        let { from, until } = this.filterOptions.date
        await this.fetchTripErrors({vehicleId: this.vehicleIdOrNull, from, until})
        this.tripErrorLoadConfig.allLoaded = true
      // if not, keep refetching trip errors with pre-defined internal date fiters (tripErrorLoadConfig)
      // until enough trip errors are loaded for a single page
      } else {
        let pageMinLoaded = false
        let refetchCount = 0
        let maxRefetchCount = this.tripErrorLoadConfig.maxRefetchCount
        let tripsCount = 0
        let dateFilter = { from: null, until: null }
        while (!pageMinLoaded && refetchCount < maxRefetchCount) {
          dateFilter = this.computeNextTripErrorDateFilter()
          let { from, until } = dateFilter
          const trips = await this.fetchTripErrors({vehicleId: this.vehicleIdOrNull, from, until, append: true})
          tripsCount += trips.length
          refetchCount++
          pageMinLoaded = tripsCount >= this.tripErrorLoadConfig.minPageSize
        }
        // if max refetch count has been reached and there still are not enough trip errors,
        // load all remaining trip errors:
        if (refetchCount >= maxRefetchCount && !pageMinLoaded) {
          await this.fetchTripErrors({vehicleId: this.vehicleIdOrNull, from: undefined, until: dateFilter.from, append: true})
          this.tripErrorLoadConfig.allLoaded = true
        }
      }
    }
  }

}
